import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    model,
    OnInit,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NavigationStart, Router } from '@angular/router';
import { SETTINGS_STORE, USER_STORE, WRAP_STORE } from '@dxp/core/stores';
import {
    TelXLButtonComponent,
    TelXLDropdownComponent,
    TelXlProgressbarComponent,
} from '@dxp/elements';
import { AgentApiService, ConversationApiService } from '@dxp/core/api';
import { CustomReuseStrategy, secondsToDuration } from '@dxp/core/helpers';
import { IDropdown, IMetric, IWorkItem } from '@dxp/core/models';
import { FormatDurationPipe } from '@dxp/core/pipes';
import { HeartbeatService } from '@dxp/core/services';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { Duration } from 'date-fns';
import { finalize, take } from 'rxjs';

@Component({
    selector: 'telxl-shared-wrap',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [
        FormsModule,
        TelXLButtonComponent,
        FormatDurationPipe,
        TelXLDropdownComponent,
        TelXlProgressbarComponent,
        TelXlProgressbarComponent,
        NgbTooltip,
    ],
    templateUrl: './wrap.component.html',
    styleUrl: './wrap.component.scss',
})
export class TelXLWrapComponent implements OnInit {
    readonly #agentApiService = inject(AgentApiService);
    readonly #conversationApiService = inject(ConversationApiService);
    readonly #heartbeatService = inject(HeartbeatService);
    readonly #customReuseStrategy = inject(CustomReuseStrategy);
    readonly #wrapStore = inject(WRAP_STORE);
    readonly #userStore = inject(USER_STORE);
    readonly #settingsStore = inject(SETTINGS_STORE);
    readonly #router = inject(Router);

    readonly #now = this.#heartbeatService.lastHeartbeat;

    workitem = input.required<IWorkItem>();

    maximumCharacterLimt = 800;

    note = model('');

    code = model('');

    summarising = signal<boolean>(false);
    summary = signal<boolean>(false);
    noSummaryMessage = signal<string | null>(null);
    loading = signal(false);

    readonly canExtend = computed<boolean>(
        () =>
            this.workitem().remainingWrapUpResets > 0 ||
            !this.workitem().remainingWrapUpResets,
    );

    readonly canComplete = computed<boolean>(() => {
        if (
            this.workitem().forceCompletionCodeSelection &&
            this.menuItems().length
        ) {
            return this.code() !== '';
        } else {
            return true;
        }
    });

    readonly hasCCOutline = computed<boolean>(() => {
        let outline = false;
        if (
            this.workitem().forceCompletionCodeSelection &&
            this.menuItems().length &&
            this.code() === ''
        ) {
            outline = true;
        } else {
            outline = false;
        }
        return outline;
    });

    readonly resetDescription = computed<string>(() => {
        if (this.workitem().remainingWrapUpResets > 1) {
            return `${this.workitem().remainingWrapUpResets} remaining resets`;
        } else if (this.workitem().remainingWrapUpResets === 1) {
            return `${this.workitem().remainingWrapUpResets} remaining reset`;
        }

        return '';
    });
    readonly wrapEnabled = computed<boolean>(() => {
        return (
            this.#settingsStore.wrap().enabled &&
            this.#settingsStore.wrap().enableWrapupTimerReset
        );
    });

    readonly taskType = computed(() =>
        this.workitem().primaryMediaType === 'Voice'
            ? 'Voice call'
            : this.workitem().primaryMediaType,
    );

    readonly isAiSummaryEnabled = computed<boolean>(
        () =>
            this.#settingsStore.isAiSummaryEnabled() &&
            this.#userStore.allowAgentsToSummariseDuringWrapUp(),
    );

    readonly isAiSummaryEnabledToolTip = computed<string | null>(() => {
        if (!this.#settingsStore.isAiSummaryEnabled())
            return 'License upgrade required';

        if (!this.#userStore.allowAgentsToSummariseDuringWrapUp())
            return 'This feature is disabled for agents';

        return null;
    });

    readonly wrapCountdown = computed<{
        duration: Duration;
        metric: IMetric;
    }>(() => {
        const workitem = this.workitem();
        const now = this.#now();

        const remainingTime =
            workitem.wrapupDurationSeconds -
            Math.round(
                (now.getTime() -
                    new Date(workitem.lastStateChangeDate).getTime()) /
                    1000,
            );

        const timeout = this.#settingsStore.wrap()?.timeoutSeconds ?? 30;

        return {
            duration: secondsToDuration(remainingTime),
            metric: {
                percent: Math.min(
                    Math.max(0, (remainingTime / timeout) * 100),
                    100,
                ),
                states: [
                    { value: 33, state: 'warning' },
                    { value: 66, state: 'success' },
                ],
            },
        };
    });
    readonly wrapCountUp = computed<Duration>(() => {
        const workitem = this.workitem();
        const now = this.#now();

        const timer = Math.round(
            (now.getTime() - new Date(workitem.lastStateChangeDate).getTime()) /
                1000,
        );

        return secondsToDuration(timer);
    });

    readonly menuItems = computed<IDropdown<string>[]>(() => {
        return (this.workitem().usableClosureCodes ?? [])
            .map(code => ({
                label: code,
                value: code,
                disabled: false,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    });

    noteCharacterLength = computed(
        () => this.note().match(/[^\s]/g)?.length ?? 0,
    );
    noteCharacterMaxLengthExceeded = computed(
        () => this.noteCharacterLength() > this.maximumCharacterLimt,
    );

    routerEvents$ = this.#router.events.pipe(takeUntilDestroyed());

    ngOnInit(): void {
        /**
         * Read from cache on initialization
         */
        this.#readSavedWrapDetails(this.workitem().workItemId);

        /**
         * Listen to the Router Events
         * To See when the URL change is happening within this component
         * Only save the details to the cache when you navigate away from the route
         */
        this.routerEvents$.subscribe(event => {
            if (event instanceof NavigationStart) {
                const redirectingToWorkItem = event.url.includes('/comms');
                const urlSplit = event.url.split('/');

                /**
                 * If the redirect is redirecting for a wrap component & the URL contains the Work Item Id
                 */
                if (redirectingToWorkItem && urlSplit.length > 3) {
                    const currentworkItemId = this.workitem().workItemId;
                    const newWorkItemId = urlSplit[3];

                    /**
                     * Save the current wrap details the cache
                     */
                    this.#saveWrapDetails(currentworkItemId);

                    /**
                     * Read from cache
                     */
                    this.#readSavedWrapDetails(newWorkItemId);
                }
            }
        });
    }

    extend() {
        this.#agentApiService.resetWrapup(this.workitem().workItemId);
    }

    complete() {
        this.#agentApiService.completeWorkItem({
            notes: this.note() ? this.note() : '',
            workItemId: this.workitem().workItemId,
            closureCode:
                this.code() && this.code().trim()
                    ? this.code()
                    : this.menuItems().length
                      ? 'No Completion Code Chosen'
                      : 'No Completion Code Available',
            notesFromAISummary: this.note().length > 0 && this.summary(),
        });
        this.#customReuseStrategy.clearCacheFor(this.routeDetails);
    }

    get routeDetails(): string {
        const id = this.workitem().workItemId;
        switch (this.workitem().primaryMediaType) {
            case 'Voice':
                return `comms/voice/${id}`;

            case 'Webchat':
                return `comms/chat/${id}`;

            case 'Email':
                return `comms/email/${id}`;

            case 'Messaging':
                return `comms/messaging/${id}`;
        }
    }

    getSummary() {
        this.loading.set(true);

        const id = this.workitem().primaryConversationId;
        const media =
            this.workitem().primaryChannelType === 'email' ? 'email' : 'chat';

        this.#conversationApiService
            .getConversationSummary(id, media)
            .pipe(
                take(1),
                finalize(() => this.loading.set(false)),
            )
            .subscribe({
                next: ({ summary }) => {
                    if (summary && summary.length) {
                        this.noSummaryMessage.set(null);
                        this.summary.set(true);
                        this.note.set(summary);
                    } else {
                        this.summary.set(false);
                        this.noSummaryMessage.set('No summary available');
                    }
                },
                error: () => {
                    this.summary.set(false);
                    this.noSummaryMessage.set('No summary available');
                },
            });
    }

    checkCharacterLimit($event: KeyboardEvent) {
        const charLength =
            this.noteCharacterLength() !== undefined
                ? this.noteCharacterLength()
                : 0;

        /**
         * Only stop allowing the user to type
         * When the char amount is greater than @param this.maximumCharacterLimt
         * Allow the keyboard actions to bypass the keyboard press prevention
         */
        if (
            charLength > this.maximumCharacterLimt &&
            !this.#allowedKeyboardActions($event.code, $event.ctrlKey)
        ) {
            $event.preventDefault();
        }
    }

    /**
     * Get the details from the cache & set the models
     */
    #readSavedWrapDetails(workItemId: string) {
        const state = this.#wrapStore.getWrapByWorkItemId(workItemId);

        this.code.set(state.code);
        this.note.set(state.notes);
        this.summary.set(state.summary);
        this.noSummaryMessage.set(state.noSummaryMessage);
    }

    /**
     * Save to wrap
     */
    #saveWrapDetails(workItemId: string) {
        this.#wrapStore.updateWrap(
            workItemId,
            this.code(),
            this.note(),
            this.summary(),
            this.noSummaryMessage(),
        );
    }

    #allowedKeyboardActions(key: string, ctrlKeyPressed: boolean): boolean {
        const mappedProperty = ctrlKeyPressed
            ? 'ctrlKeyPressed'
            : 'nonCtrlKeyPressed';
        const actions = {
            ctrlKeyPressed: ['KeyA', 'KeyC', 'KeyV'],
            nonCtrlKeyPressed: ['Delete', 'Enter', 'Backspace'],
        };

        return !!actions[mappedProperty].find(allowedKey => allowedKey === key);
    }
}
